import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Typography,
  Card,
  CardContent,
  CircularProgress,
  Alert,
  Button,
} from '@mui/material';
import {
  TrendingUp,
  TrendingDown,
  Analytics,
  Refresh,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { cryptoService, OverviewData, CryptoData } from '../../services/cryptoService';
import { authService } from '../../services/authService';
import { androidColors } from '../../theme/androidTheme';
import Layout from '../../components/Layout/Layout';
import OverviewChart from '../../components/Charts/OverviewChart';
import { cryptoIconsService } from '../../services/cryptoIconsService';
import PageTitle from '../../components/PageTitle/PageTitle';

const Insights: React.FC = () => {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [overviewData, setOverviewData] = useState<OverviewData>({
    totalCapital: 0,
    gain: 0,
    gainPercentage: 0,
    stackedIncome: 0,
    airdropIncome: 0,
  });
  const [topPerformers, setTopPerformers] = useState<CryptoData[]>([]);
  const [cryptoIcons, setCryptoIcons] = useState<{ [key: string]: string }>({});

  const loadInsightsData = useCallback(async () => {
    setLoading(true);
    setError('');

    try {
      const [overviewResult, portfolioResult] = await Promise.all([
        cryptoService.getOverviewData(),
        cryptoService.getPortfolioData()
      ]);
      
      setOverviewData(overviewResult);
      
      // Trier les cryptos par performance (gain/perte pourcentage)
      const sortedCryptos = [...portfolioResult.cryptos]
        .sort((a, b) => b.gain_perte_pourcentage - a.gain_perte_pourcentage)
        .slice(0, 5); // Top 5
      
      setTopPerformers(sortedCryptos);

      // Charger les icônes pour chaque crypto
      const icons: { [key: string]: string } = {};
      for (const crypto of sortedCryptos) {
        const iconUrl = await cryptoIconsService.getCryptoIconUrl(crypto.symbol);
        if (iconUrl) {
          icons[crypto.symbol] = iconUrl;
        }
      }
      setCryptoIcons(icons);
    } catch (err: any) {
      console.error('Erreur insights:', err);
      setError(err.message || 'Erreur lors du chargement des données');
      
      if (err.message?.includes('Session expirée') || err.message?.includes('Token JWT manquant')) {
        authService.logout();
        navigate('/login');
      }
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  useEffect(() => {
    loadInsightsData();
  }, [loadInsightsData]);

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat(i18n.language === 'fr' ? 'fr-FR' : undefined, {
      style: 'currency',
      currency: 'EUR',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount);
  };

  const formatPercentage = (percentage: number) => {
    return `${percentage >= 0 ? '+' : ''}${percentage.toFixed(2)}%`;
  };

  if (loading) {
    return (
      <Layout title={t('insights.title')}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: '60vh',
          }}
        >
          <CircularProgress sx={{ color: androidColors.primaryGold }} />
        </Box>
      </Layout>
    );
  }

  if (error) {
    return (
      <Layout title={t('insights.title')}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: '60vh',
          }}
        >
          <Alert
            severity="error"
            sx={{
              backgroundColor: 'rgba(244, 67, 54, 0.1)',
              border: '1px solid rgba(244, 67, 54, 0.3)',
              color: androidColors.error,
              '& .MuiAlert-icon': {
                color: androidColors.error,
              },
            }}
            action={
              <Button
                color="inherit"
                size="small"
                onClick={loadInsightsData}
                startIcon={<Refresh />}
              >
                {t('common.retry')}
              </Button>
            }
          >
            {error}
          </Alert>
        </Box>
      </Layout>
    );
  }

  return (
    <Layout title={t('insights.pageTitle')}>
      <PageTitle title={t('insights.pageTitle')} />
      {/* Graphique d'évolution */}
      <Box sx={{ mb: 4 }}>
        <OverviewChart overviewData={overviewData} />
      </Box>

      {/* Statistiques clés */}
      <Box sx={{ mb: 4 }}>
        <Typography
          variant="h5"
          sx={{
            color: androidColors.textColor,
            fontWeight: 'bold',
            mb: 3,
          }}
        >
          {t('insights.keyStats')}
        </Typography>
        
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3 }}>
          <Box sx={{ flex: { xs: '1 1 100%', sm: '1 1 calc(50% - 12px)', md: '1 1 calc(33.333% - 16px)' } }}>
            <Card
              sx={{
                backgroundColor: androidColors.cardBackground,
                border: `1px solid ${androidColors.borderColor}`,
                borderRadius: '12px',
                p: 2,
              }}
            >
              <CardContent sx={{ p: 0 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <Analytics sx={{ color: androidColors.primaryGold, mr: 1 }} />
                  <Typography
                    variant="h6"
                    sx={{
                      color: androidColors.textColor,
                      fontWeight: 'bold',
                    }}
                  >
                    {t('insights.globalPerformance')}
                  </Typography>
                </Box>
                <Typography
                  variant="h4"
                  sx={{
                    color: overviewData.gain >= 0 ? androidColors.success : androidColors.error,
                    fontWeight: 'bold',
                    mb: 1,
                  }}
                >
                  {formatPercentage(overviewData.gainPercentage)}
                </Typography>
                <Typography
                  variant="body2"
                  sx={{ color: androidColors.textSecondary }}
                >
                  {t('insights.gainLossAmount', { amount: formatCurrency(overviewData.gain) })}
                </Typography>
              </CardContent>
            </Card>
          </Box>

          <Box sx={{ flex: { xs: '1 1 100%', sm: '1 1 calc(50% - 12px)', md: '1 1 calc(33.333% - 16px)' } }}>
            <Card
              sx={{
                backgroundColor: androidColors.cardBackground,
                border: `1px solid ${androidColors.borderColor}`,
                borderRadius: '12px',
                p: 2,
              }}
            >
              <CardContent sx={{ p: 0 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <TrendingUp sx={{ color: androidColors.success, mr: 1 }} />
                  <Typography
                    variant="h6"
                    sx={{
                      color: androidColors.textColor,
                      fontWeight: 'bold',
                    }}
                  >
                    {t('insights.stakedRevenues')}
                  </Typography>
                </Box>
                <Typography
                  variant="h4"
                  sx={{
                    color: androidColors.success,
                    fontWeight: 'bold',
                    mb: 1,
                  }}
                >
                  {formatCurrency(overviewData.stackedIncome)}
                </Typography>
                <Typography
                  variant="body2"
                  sx={{ color: androidColors.textSecondary }}
                >
                  {t('insights.stakingRevenues')}
                </Typography>
              </CardContent>
            </Card>
          </Box>

          <Box sx={{ flex: { xs: '1 1 100%', sm: '1 1 calc(50% - 12px)', md: '1 1 calc(33.333% - 16px)' } }}>
            <Card
              sx={{
                backgroundColor: androidColors.cardBackground,
                border: `1px solid ${androidColors.borderColor}`,
                borderRadius: '12px',
                p: 2,
              }}
            >
              <CardContent sx={{ p: 0 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <TrendingUp sx={{ color: androidColors.primaryGold, mr: 1 }} />
                  <Typography
                    variant="h6"
                    sx={{
                      color: androidColors.textColor,
                      fontWeight: 'bold',
                    }}
                  >
                    {t('insights.airdropRevenues')}
                  </Typography>
                </Box>
                <Typography
                  variant="h4"
                  sx={{
                    color: androidColors.primaryGold,
                    fontWeight: 'bold',
                    mb: 1,
                  }}
                >
                  {formatCurrency(overviewData.airdropIncome)}
                </Typography>
                <Typography
                  variant="body2"
                  sx={{ color: androidColors.textSecondary }}
                >
                  {t('insights.airdrops')}
                </Typography>
              </CardContent>
            </Card>
          </Box>
        </Box>
      </Box>

      {/* Top performers */}
      {topPerformers.length > 0 && (
        <Box>
          <Typography
            variant="h5"
            sx={{
              color: androidColors.textColor,
              fontWeight: 'bold',
              mb: 3,
            }}
          >
            {t('insights.topPerformers')}
          </Typography>
          
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {topPerformers.map((crypto, index) => (
              <Card
                key={`${crypto.symbol}-${index}`}
                sx={{
                  backgroundColor: androidColors.cardBackground,
                  border: `1px solid ${androidColors.borderColor}`,
                  borderRadius: '12px',
                  p: 2,
                }}
              >
                <CardContent sx={{ p: 0 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <Box
                        sx={{
                          width: 40,
                          height: 40,
                          borderRadius: '50%',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          mr: 2,
                          overflow: 'hidden',
                        }}
                      >
                        <img
                          src={cryptoIcons[crypto.symbol] || ''}
                          alt={crypto.symbol}
                          style={{
                            width: '100%',
                            height: '100%',
                            objectFit: 'cover',
                          }}
                          onError={(e) => {
                            // En cas d'erreur de chargement de l'image, afficher la première lettre
                            const target = e.target as HTMLImageElement;
                            target.style.display = 'none';
                            const parent = target.parentElement;
                            if (parent) {
                              const fallback = document.createElement('div');
                              fallback.style.width = '100%';
                              fallback.style.height = '100%';
                              fallback.style.display = 'flex';
                              fallback.style.alignItems = 'center';
                              fallback.style.justifyContent = 'center';
                              fallback.style.backgroundColor = androidColors.primaryGold;
                              fallback.style.color = androidColors.darkBackground;
                              fallback.style.fontWeight = 'bold';
                              fallback.textContent = crypto.symbol.charAt(0);
                              parent.appendChild(fallback);
                            }
                          }}
                        />
                      </Box>
                      <Box>
                        <Typography
                          variant="h6"
                          sx={{
                            color: androidColors.textColor,
                            fontWeight: 'bold',
                          }}
                        >
                          {crypto.symbol.toUpperCase()}
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{ color: androidColors.textSecondary }}
                        >
                          {crypto.name}
                        </Typography>
                      </Box>
                    </Box>
                    
                    <Box sx={{ textAlign: 'right' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                        {crypto.gain_perte_pourcentage >= 0 ? (
                          <TrendingUp sx={{ color: androidColors.success, mr: 1, fontSize: 20 }} />
                        ) : (
                          <TrendingDown sx={{ color: androidColors.error, mr: 1, fontSize: 20 }} />
                        )}
                        <Typography
                          variant="h6"
                          sx={{
                            color: crypto.gain_perte_pourcentage >= 0 ? androidColors.success : androidColors.error,
                            fontWeight: 'bold',
                          }}
                        >
                          {formatPercentage(crypto.gain_perte_pourcentage)}
                        </Typography>
                      </Box>
                      <Typography
                        variant="body2"
                        sx={{ color: androidColors.textSecondary }}
                      >
                        {formatCurrency(crypto.gain_perte)}
                      </Typography>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            ))}
          </Box>
        </Box>
      )}
    </Layout>
  );
};

export default Insights;